// @oasis-permission-inherits: /jerboa (jerboa:view)
import { Link, createFileRoute } from "@tanstack/react-router";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { copyToClipboard } from "~/lib/clipboard";
import { formatDurationMs } from "~/modules/jerboa-client-export/formatters";
import { getAiEditFxShadowQuery } from "~/modules/jerboa/ai-edit-fx-shadowing-queries";
import { AiEditFxShadowingPlayer } from "~/modules/jerboa/components/ai-edit-fx-shadowing-player";
import { JerboaCommitLink } from "~/modules/jerboa/components/user-env-card";

function isFailureStatus(status: string) {
  return status.endsWith("_FAILED") || status === "VERSION_MISMATCH";
}

function statusBadgeVariant(status: string) {
  if (status === "SUCCESS") {
    return "default" as const;
  }
  if (status === "CANCELLED") {
    return "secondary" as const;
  }
  return "outline" as const;
}

export const Route = createFileRoute("/_dash/jerboa/ai-edit-fx-shadowing/$resultId")({
  component: RouteComponent,
});

function Version({ value }: { value: string | null | undefined }) {
  return value ? <JerboaCommitLink sha={value} /> : "—";
}

function DetailField({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm">{children}</div>
    </div>
  );
}

function Artifact({ label, uri }: { label: string; uri: string | null | undefined }) {
  if (!uri) {
    return null;
  }
  return (
    <DetailField label={label} className="min-w-0">
      <div className="flex items-start gap-2">
        <code className="min-w-0 break-all text-xs">{uri}</code>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 shrink-0 px-2 text-xs"
          onClick={() => void copyToClipboard(uri, "Copied GCS URI")}
        >
          Copy
        </Button>
      </div>
    </DetailField>
  );
}

function RouteComponent() {
  const { resultId } = Route.useParams();
  const { data, error, isLoading } = getAiEditFxShadowQuery.useQuery({
    pathParams: { resultId },
  });
  const result = data?.result;
  return (
    <div className="space-y-6">
      <Link to="/jerboa/ai-edit-fx-shadowing" className="text-sm underline">
        ← AI Edit FX Shadowing
      </Link>
      <h1 className="text-xl font-bold">AI Edit FX comparison</h1>
      {isLoading && <p>Loading comparison…</p>}
      {error && (
        <p role="alert" className="text-destructive">
          Could not load this record. It may no longer exist.
        </p>
      )}
      {result && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Run Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <DetailField label="Status">
                  <Badge
                    variant={statusBadgeVariant(result.status)}
                    className={
                      isFailureStatus(result.status)
                        ? "border-destructive/40 text-xs text-destructive"
                        : "text-xs"
                    }
                  >
                    {result.status}
                  </Badge>
                </DetailField>
                <DetailField label="Stage">{result.stage}</DetailField>
                <DetailField label="Jerboa Version">
                  <Version value={result.jerboaVersion} />
                </DetailField>
                <DetailField label="Created">
                  {new Date(result.createdAt).toLocaleString()}
                </DetailField>
                <DetailField label="Updated">
                  {new Date(result.updatedAt).toLocaleString()}
                </DetailField>
                <DetailField label="Duration">
                  {formatDurationMs(
                    result.durationSeconds == null ? null : result.durationSeconds * 1000
                  )}
                </DetailField>
                {result.editPlanId && (
                  <DetailField label="Edit Plan ID" className="min-w-0">
                    <code className="break-all text-xs">{result.editPlanId}</code>
                  </DetailField>
                )}
                <Artifact label="Source Project URI" uri={result.sourceProjectProtoUri} />
                <DetailField label="Shadow Workflow ID" className="min-w-0">
                  <code className="break-all text-xs">{result.workflowId}</code>
                </DetailField>
                <DetailField label="Shadow Workflow Run ID" className="min-w-0">
                  <code className="break-all text-xs">{result.workflowRunId}</code>
                </DetailField>
                <DetailField label="Generation Workflow ID" className="min-w-0">
                  <code className="break-all text-xs">{result.sourceWorkflowId}</code>
                </DetailField>
                <DetailField label="Generation Workflow Run ID" className="min-w-0">
                  <code className="break-all text-xs">{result.sourceRunId}</code>
                </DetailField>
              </div>
              <div className="border-t pt-4">
                <div className="text-xs text-muted-foreground">Export Settings</div>
                <pre className="mt-2 overflow-auto rounded bg-muted p-3 text-xs">
                  {JSON.stringify(result.exportSettings, null, 2)}
                </pre>
              </div>
              {result.error && (
                <pre className="whitespace-pre-wrap break-words rounded bg-destructive/10 p-3 text-sm text-destructive">
                  {result.error}
                </pre>
              )}
            </CardContent>
          </Card>
          <AiEditFxShadowingPlayer
            originalVideoUrl={result.originalVideoUrl}
            migratedVideoUrl={result.migratedVideoUrl}
            timeline={data.comparisonTimeline}
            timelineError={data.comparisonTimelineError}
          />
          <div className="grid gap-6 lg:grid-cols-2">
            {[
              {
                label: "Original Render",
                render: result.original,
                url: result.originalVideoUrl,
                uri: result.originalProjectProtoUri,
              },
              {
                label: "FX Render",
                render: result.migrated,
                url: result.migratedVideoUrl,
                uri: result.migratedProjectProtoUri,
              },
            ].map(({ label, render, url, uri }) => (
              <Card key={label}>
                <CardHeader>
                  <CardTitle>{label}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <p className="text-muted-foreground">
                    {url
                      ? "Video is available in the synchronized viewer above."
                      : "Video unavailable or export not completed."}
                  </p>
                  <div className="break-all">
                    Operation: <code>{render?.operationId ?? "Not submitted"}</code>
                  </div>
                  <div className="break-all">
                    Video asset: <code>{render?.videoAssetId ?? "None"}</code>
                  </div>
                  {render?.error && <p className="break-words text-destructive">{render.error}</p>}
                  <Artifact label="Project artifact" uri={uri} />
                </CardContent>
              </Card>
            ))}
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Pixel comparison</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {result.comparison ? (
                <>
                  <div>Algorithm: {result.comparison.algorithm}</div>
                  <div>
                    Similarity: {result.comparison.similarity.toFixed(4)} · Deviation:{" "}
                    {result.comparison.deviation.toFixed(4)}
                  </div>
                  <div>
                    {result.comparison.comparedFrames} sampled frames over{" "}
                    {result.comparison.comparedDurationSecs.toFixed(2)} seconds
                  </div>
                  <div>
                    Frame similarity range: {result.comparison.minFrameSimilarity.toFixed(4)}–
                    {result.comparison.maxFrameSimilarity.toFixed(4)}
                  </div>
                  <Artifact label="Full comparison JSON" uri={result.comparison.artifactUri} />
                </>
              ) : (
                <p>No comparison available.</p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
